import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  ChecklistItemDtoStatus,
  usePatchUserAgentsConfig,
  UserAgentsConfigResponse
} from '@/api/generated';
import {
  AgentConfigType,
  AgentsListConfig
} from '@/components/Agents/AgentConfig/AgentsListConfig';
import { useCheckList } from '@/hooks/useCheckList';
import { useUserAgents } from '@/hooks/useUserAgents';
import { CheckListEnumTitles } from '@/pages/HomePage/components/checklist/CheckListConfig';

export const useNativeAgents = () => {
  const [selectedNativeAgent, setSelectedNativeAgent] = useState<AgentConfigType | null>(null);
  const queryClient = useQueryClient();
  const { data: agents } = useUserAgents();
  const isMeetingAgentActive = agents?.items?.[0]?.meetingSummary?.enabled;
  const isDelegationAgentActive = agents?.items?.at(0)?.taskFollowUp?.enabled;
  const { mutate: patchUserAgentsConfig } = usePatchUserAgentsConfig();
  const { isItemCompleted, updateCheckList } = useCheckList();
  const isAtLeastOneAgentCompleted = isItemCompleted(CheckListEnumTitles.ActivateOneAgent);

  const toggleUserAgentsConfig = (agentKey: string | undefined, enabled: boolean) => {
    if (!agentKey) return;
    let oldData: UserAgentsConfigResponse;
    queryClient.setQueryData(['userAgentsConfig'], (old: UserAgentsConfigResponse) => {
      oldData = old;
      if (!old.items) return old;
      let newData = old;
      if (agentKey === 'meetingSummary') {
        newData = {
          items: [
            {
              ...old.items[0],
              meetingSummary: { ...old.items[0].meetingSummary, enabled }
            }
          ]
        };
      } else if (agentKey === 'taskFollowUp') {
        newData = {
          items: [
            {
              ...old.items[0],
              taskFollowUp: { ...old.items[0].taskFollowUp, enabled }
            }
          ]
        };
      }
      return newData;
    });

    patchUserAgentsConfig(
      {
        data: {
          [agentKey]: { enabled }
        }
      },
      {
        onSuccess: () => {
          if (isAtLeastOneAgentCompleted) return;
          updateCheckList({
            title: CheckListEnumTitles.ActivateOneAgent,
            status: ChecklistItemDtoStatus.completed
          });
        },
        onError: () => {
          queryClient.setQueryData(['userAgentsConfig'], oldData);
        }
      }
    );
  };

  const smartTemplateAgent = {
    id: AgentsListConfig[0].activeKey,
    name: AgentsListConfig[0].agentName,
    description: AgentsListConfig[0].agentDescription,
    trigger: '',
    lastEdited: '',
    enabled: isMeetingAgentActive ?? false
  };

  const taskFollowUpAgent = {
    id: AgentsListConfig[1].activeKey,
    name: AgentsListConfig[1].agentName,
    description: AgentsListConfig[1].agentDescription,
    trigger: '',
    lastEdited: '',
    enabled: isDelegationAgentActive ?? false
  };

  return {
    selectedNativeAgent,
    setSelectedNativeAgent,
    smartTemplateAgent,
    taskFollowUpAgent,
    toggleUserAgentsConfig
  };
};
